/**
 * WritingDeskPanel — the desk's settings, over the engine's category registry.
 *
 * Spec §9.2 names the fields; §9.3 resolves their scope: under the tenant-is-a-
 * person ruling these ARE per-person settings, so there is no second preference
 * store and this panel is the only place they are edited.
 *
 * Project-scoped fields are the defaults a NEW project is created with. Changing
 * one here does not reach into a project that already exists — its settings
 * travel in the project representation, which is the store's to change.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { SettingsSection } from '@seitiate/settings-ui';
import { settings as engineSettings } from '@/api/engine';
import {
  DESK_SETTINGS_CATEGORY,
  DESK_SETTINGS_DEFAULTS,
  readDeskSettings,
  type DeskSettings,
} from '@/features/desk/settings/deskSettings';

const CUSTODY_TIERS: Array<{ value: DeskSettings['custody_tier']; label: string; note: string }> = [
  { value: 'managed', label: 'Managed', note: 'Encrypted under your seity key, on every device.' },
  {
    value: 'sovereign',
    label: 'Sovereign',
    note: 'User-held keys. Available once the sovereignty forge work lands.',
  },
  {
    value: 'local_only',
    label: 'Local only',
    note: 'Master copy on your disk. Available once the local desk runtime lands.',
  },
];

export const WritingDeskPanel: React.FC = () => {
  const [values, setValues] = useState<DeskSettings>(DESK_SETTINGS_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    engineSettings
      .getCategory(DESK_SETTINGS_CATEGORY)
      .then((response) => {
        if (!cancelled) setValues(readDeskSettings(response.settings));
      })
      .catch(() => {
        // An engine that does not yet carry the category is not an error the
        // writer needs to see; the defaults are honest and the first save
        // creates it.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const update = useCallback(async <K extends keyof DeskSettings>(key: K, value: DeskSettings[K]) => {
    setValues((current) => ({ ...current, [key]: value }));
    setSaving(true);
    setError(null);
    try {
      const response = await engineSettings.updateCategory(DESK_SETTINGS_CATEGORY, { [key]: value });
      setValues(readDeskSettings(response.settings));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That setting could not be saved');
    } finally {
      setSaving(false);
    }
  }, []);

  if (loading) return <p className="text-xs text-gray-400">Loading desk settings…</p>;

  return (
    <div className="space-y-4">
      {error ? <p className="text-xs" style={{ color: 'var(--sp-error)' }}>{error}</p> : null}

      <SettingsSection title="Writing">
        <label className="flex items-center justify-between gap-4 py-1">
          <span className="text-sm">
            Ask me to declare a paste
            <span className="block text-xs text-gray-400">
              The desk records what you say about text you paste, and infers nothing from the paste
              itself.
            </span>
          </span>
          <input
            type="checkbox"
            checked={values.declaration_prompt_on_paste}
            disabled={saving}
            onChange={(event) => void update('declaration_prompt_on_paste', event.target.checked)}
          />
        </label>

        <label className="flex items-center justify-between gap-4 py-1">
          <span className="text-sm">
            Show the provenance view by default
            <span className="block text-xs text-gray-400">
              Underlines each span by the mode its writer declared.
            </span>
          </span>
          <input
            type="checkbox"
            checked={values.provenance_view_default}
            disabled={saving}
            onChange={(event) => void update('provenance_view_default', event.target.checked)}
          />
        </label>

        <label className="flex items-center justify-between gap-4 py-1">
          <span className="text-sm">
            Autosave interval
            <span className="block text-xs text-gray-400">Seconds of quiet before a draft is saved.</span>
          </span>
          <input
            type="number"
            min={1}
            max={600}
            className="w-20 bg-transparent border rounded px-2 py-1 text-sm"
            style={{ borderColor: 'var(--sp-border)' }}
            value={values.autosave_interval_s}
            disabled={saving}
            onChange={(event) => void update('autosave_interval_s', Number(event.target.value) || 3)}
          />
        </label>

        <label className="flex items-center justify-between gap-4 py-1">
          <span className="text-sm">
            Source view is editable
            <span className="block text-xs text-gray-400">
              Markdown writeback is not built yet; this stays off until it is.
            </span>
          </span>
          <input type="checkbox" checked={values.source_view_editable} disabled readOnly />
        </label>
      </SettingsSection>

      <SettingsSection title="New project defaults" defaultExpanded={false}>
        <fieldset className="py-1">
          <legend className="text-sm">Custody</legend>
          {CUSTODY_TIERS.map((tier) => (
            <label key={tier.value} className="flex items-start gap-2 py-1">
              <input
                type="radio"
                name="desk-custody-tier"
                checked={values.custody_tier === tier.value}
                disabled={saving || tier.value !== 'managed'}
                onChange={() => void update('custody_tier', tier.value)}
              />
              <span className="text-sm">
                {tier.label}
                <span className="block text-xs text-gray-400">{tier.note}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <label className="flex items-center justify-between gap-4 py-1">
          <span className="text-sm">
            Cloud sync
            <span className="block text-xs text-gray-400">
              Keep a new project's documents on every device through the managed store.
            </span>
          </span>
          <input
            type="checkbox"
            checked={values.cloud_sync}
            disabled={saving}
            onChange={(event) => void update('cloud_sync', event.target.checked)}
          />
        </label>

        <label className="flex items-center justify-between gap-4 py-1">
          <span className="text-sm">Citation style</span>
          <input
            className="w-40 bg-transparent border rounded px-2 py-1 text-sm"
            style={{ borderColor: 'var(--sp-border)' }}
            value={values.citation_style}
            disabled={saving}
            onChange={(event) => void update('citation_style', event.target.value)}
          />
        </label>

        <label className="flex items-center justify-between gap-4 py-1">
          <span className="text-sm">
            Snapshots kept
            <span className="block text-xs text-gray-400">Per document, newest first.</span>
          </span>
          <input
            type="number"
            min={1}
            className="w-24 bg-transparent border rounded px-2 py-1 text-sm"
            style={{ borderColor: 'var(--sp-border)' }}
            value={values.snapshot_retention_count}
            disabled={saving}
            onChange={(event) =>
              void update('snapshot_retention_count', Number(event.target.value) || 200)
            }
          />
        </label>

        <label className="flex items-center justify-between gap-4 py-1">
          <span className="text-sm">Snapshots kept for (days)</span>
          <input
            type="number"
            min={1}
            className="w-24 bg-transparent border rounded px-2 py-1 text-sm"
            style={{ borderColor: 'var(--sp-border)' }}
            value={values.snapshot_retention_days}
            disabled={saving}
            onChange={(event) =>
              void update('snapshot_retention_days', Number(event.target.value) || 365)
            }
          />
        </label>
      </SettingsSection>

      <SettingsSection title="Not yet built" defaultExpanded={false}>
        <p className="text-xs text-gray-400">
          Dictation ({values.dictation_provider}), export templates, and the agent model default are
          recorded here so the settings shape does not change when those slices land. They have no
          effect until they do.
        </p>
      </SettingsSection>
    </div>
  );
};
